import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Plus, ClipboardList, FolderOpen } from 'lucide-react';
import { Button, PageHeader } from '../../../components';
import { qualityService } from '../../../services';
import { errorMessage } from './shared';
import AuditRegister from './AuditRegister';
import AuditWorkspace from './AuditWorkspace';
import AuditFormModal from './AuditFormModal';
import FollowUpActions from './FollowUpActions';
import PolicyLibrary from './PolicyLibrary';
import PolicyEditor from './PolicyEditor';
import PolicyViewer from './PolicyViewer';
import CategoriesModal from './CategoriesModal';

/**
 * Audits & policies — one page, two halves.
 *
 * The two belong together because an audit checks the service against a
 * policy, and the link runs both ways: an audit records the policy version it
 * was checked against, and a policy is usually opened FROM the audit that
 * cites it. Splitting them into two menu items would mean a manager leaving
 * their half-finished audit to go and read the policy it is about.
 *
 * The section lives in the URL rather than in state, so an audit can be linked
 * to, reloaded, and returned to by the browser's back button:
 *
 *   /manager/audits-policies                  the register
 *   /manager/audits-policies/actions          follow-up actions (?audit= to scope)
 *   /manager/audits-policies/audits/:auditId  one audit's workspace
 *   /manager/audits-policies/policies         the policy library
 *   /manager/audits-policies/policies/new     the editor, new policy
 *   /manager/audits-policies/policies/:id/edit the editor, a draft revision
 *
 * Access works like every other module: managers hold it by role, and anybody
 * else once they are granted the Audits & Policies menu — so a manager can
 * hand the module to a senior carer or quality lead. The API applies the same
 * isManager() gate.
 *
 * Whoever acts is who gets recorded: sign-off stamps the audit with
 * signed_by_name and publishing stamps the version with published_by_name,
 * both taken from the token. Delegating the access never disguises who did
 * the work.
 */
const AuditsPolicies = ({ section = 'audits' }) => {
  const navigate = useNavigate();
  const { auditId, policyId } = useParams();
  const [searchParams] = useSearchParams();

  const [meta, setMeta] = useState(null);
  const [audits, setAudits] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [policySummary, setPolicySummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);

  const [auditForm, setAuditForm] = useState(null);     // { audit? } — null when closed
  const [viewPolicy, setViewPolicy] = useState(null);   // { id, version }
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  // Bumped when an audit's details are edited from inside its own workspace.
  // The workspace holds its own copy of the audit, so without this the header
  // would keep showing the owner and due date the manager just changed.
  const [workspaceToken, setWorkspaceToken] = useState(0);

  const onPolicies = section.startsWith('policies');
  const scopedAuditId = searchParams.get('audit');
  // Which policy to open, carried in the URL rather than in state. Saving a
  // draft navigates away from the editor, and a component that has just been
  // unmounted cannot open a dialog on the page that replaces it — so the
  // instruction travels with the navigation instead.
  const viewPolicyParam = searchParams.get('view');

  const loadAudits = useCallback(async () => {
    const response = await qualityService.getAudits();
    setAudits(response.data || []);
    setAuditSummary(response.summary || null);
  }, []);

  const loadPolicies = useCallback(async () => {
    const response = await qualityService.getPolicies();
    setPolicies(response.data || []);
    setPolicySummary(response.summary || null);
    setCategories(response.categories || []);
    setTemplates(response.templates || {});
  }, []);

  // Both halves load together. The audit forms need the policy list for their
  // link dropdown, and the header counts are shown whichever tab is open, so
  // loading lazily would only trade one request for a flicker.
  useEffect(() => {
    let cancelled = false;
    Promise.all([qualityService.getAuditMeta(), loadAudits(), loadPolicies()])
      .then(([metaResponse]) => { if (!cancelled) setMeta(metaResponse.data); })
      .catch((error) => { if (!cancelled) toast.error(errorMessage(error, 'Audits & policies could not be loaded.')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadAudits, loadPolicies]);

  const refreshAudits = useCallback(() => {
    loadAudits().catch(() => { /* a failed background refresh is not worth a toast */ });
  }, [loadAudits]);

  const refreshPolicies = useCallback(() => {
    // The audit forms offer policies to link to, so republishing one has to
    // reach the meta as well or the dropdown shows a stale version number.
    loadPolicies().catch(() => {});
    qualityService.getAuditMeta().then((response) => setMeta(response.data)).catch(() => {});
  }, [loadPolicies]);

  const go = (path) => navigate(`/manager/audits-policies${path}`);

  // A policy asked for by URL wins over one opened by clicking a row, so a
  // freshly saved draft opens itself exactly once and closing it clears the
  // parameter rather than reopening on the next render.
  const openPolicy = viewPolicyParam
    ? { id: viewPolicyParam, version: searchParams.get('version') || 'current' }
    : viewPolicy;

  const closePolicy = () => {
    setViewPolicy(null);
    if (viewPolicyParam) go('/policies');
  };

  if (loading) {
    return <div className="py-24 text-center text-[13px] text-[#64748B] dark:text-gray-400">Loading audits &amp; policies…</div>;
  }

  // The editor is a full-page workspace, not a panel inside the tabs: writing
  // a policy is a long job and the tab bar above it is only a distraction.
  if (section === 'policies-editor') {
    return (
      <>
        <PolicyEditor
          policyId={policyId || null}
          categories={categories}
          templates={templates}
          onClose={() => go('/policies')}
          onManageCategories={() => setCategoriesOpen(true)}
          onSaved={(policy) => {
            refreshPolicies();
            // Straight into the draft they just saved, so the next step —
            // review and publish — is in front of them rather than another
            // click away in the library.
            go(policy?.id ? `/policies?view=${policy.id}&version=draft` : '/policies');
          }}
        />
        <CategoriesModal
          open={categoriesOpen}
          onClose={() => setCategoriesOpen(false)}
          onChanged={refreshPolicies}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audits & policies"
        subtitle="Plan checks, follow up and keep policies up to date."
        actions={
          <Button
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => (onPolicies ? go('/policies/new') : setAuditForm({}))}
          >
            {onPolicies ? 'Add policy' : 'New audit'}
          </Button>
        }
      />

      <nav className="inline-flex gap-1 p-1 bg-[#E7EDF5] dark:bg-gray-800 border border-[#DCE5F1] dark:border-gray-700 rounded-[11px]" aria-label="Quality sections">
        {[
          ['audits', 'Audits', ClipboardList, '/'],
          ['policies', 'Policies', FolderOpen, '/policies'],
        ].map(([value, label, Icon, path]) => {
          const active = value === 'policies' ? onPolicies : !onPolicies;
          return (
            <button
              key={value}
              type="button"
              aria-current={active ? 'page' : 'false'}
              onClick={() => go(path)}
              className={`inline-flex items-center gap-2 px-4 h-9 rounded-[7px] text-[12.5px] font-semibold transition-colors ${
                active
                  ? 'bg-white dark:bg-[#262b33] text-blue-600 shadow-[0_1px_4px_rgba(24,55,100,0.05)]'
                  : 'text-[#64738B] dark:text-gray-400'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          );
        })}
      </nav>

      {section === 'audits' && (
        <AuditRegister
          audits={audits}
          summary={auditSummary}
          onNewAudit={() => setAuditForm({})}
          onOpenAudit={(id) => go(`/audits/${id}`)}
          onOpenActions={(id) => go(id ? `/actions?audit=${id}` : '/actions')}
        />
      )}

      {section === 'audit' && (
        <AuditWorkspace
          key={`${auditId}:${workspaceToken}`}
          auditId={auditId}
          meta={meta}
          // Reload the register on the way back rather than after every
          // answer — the progress bars and statuses need to be right when the
          // manager actually looks at them.
          onBack={() => { refreshAudits(); go('/'); }}
          onChanged={refreshAudits}
          onEdit={(audit) => setAuditForm({ audit })}
          onOpenActions={(id) => go(id ? `/actions?audit=${id}` : '/actions')}
          onOpenPolicy={(id) => setViewPolicy({ id, version: 'current' })}
        />
      )}

      {section === 'actions' && (
        <FollowUpActions
          meta={meta}
          scopeAuditId={scopedAuditId}
          scopeTitle={audits.find((a) => String(a.id) === String(scopedAuditId))?.title}
          onBack={(target) => go(target === 'actions' ? '/actions' : '/')}
          onOpenAudit={(id) => go(`/audits/${id}`)}
          onChanged={refreshAudits}
        />
      )}

      {section === 'policies' && (
        <PolicyLibrary
          policies={policies}
          summary={policySummary}
          categories={categories}
          onNew={() => go('/policies/new')}
          onOpen={(id) => setViewPolicy({ id, version: 'current' })}
          onManageCategories={() => setCategoriesOpen(true)}
        />
      )}

      {auditForm && (
        <AuditFormModal
          open
          onClose={() => setAuditForm(null)}
          meta={meta}
          audit={auditForm.audit}
          onSaved={(audit) => {
            refreshAudits();
            if (auditForm.audit) {
              // Edited from the workspace — make it re-read the audit so the
              // header matches what was just saved.
              setWorkspaceToken((n) => n + 1);
              return;
            }
            // A brand-new audit opens straight into its workspace — the
            // manager created it in order to work through it.
            if (audit?.id) go(`/audits/${audit.id}`);
          }}
        />
      )}

      {openPolicy && (
        <PolicyViewer
          key={`${openPolicy.id}:${openPolicy.version}`}
          policyId={openPolicy.id}
          initialVersion={openPolicy.version}
          onClose={closePolicy}
          onChanged={() => { refreshPolicies(); refreshAudits(); }}
          onEdit={(id) => { setViewPolicy(null); go(`/policies/${id}/edit`); }}
        />
      )}

      <CategoriesModal
        open={categoriesOpen}
        onClose={() => setCategoriesOpen(false)}
        onChanged={refreshPolicies}
      />
    </div>
  );
};

export default AuditsPolicies;
